import React from 'react';
import PropTypes from 'prop-types';
import { withConfig } from 'HOCs/withConfig/withConfig';

/** HOC that decides what component will be rendered according to a configuration value.
 *
 * This takes into account deciding what `fetchData` to call in the server side.
 *
 * @param {Array} key - Key used to query the configuration
 * @param {Object{{Value} -> {Component}} components - Mapping of value to component
 * @param {Component} Fallback - Fallback component if no from `components` has matched
 */
const ifConfig = (key, components, Fallback) => {
    const IfConfig = ({ __config, ...props }) => {
        const value = __config.get(...key);
        const Component = components[value];

        if (Component) {
            return <Component { ...props } />;
        }
        else if (Fallback) {
            return <Fallback { ...props } />;
        }

        return null;
    };

    IfConfig.propTypes = {
        __config: PropTypes.shape({
            get: PropTypes.func.isRequired
        }).isRequired
    };

    IfConfig.fetchData = (dispatch, renderProps, reqProps, options, ...args) => {
        const { config } = options;
        const value = key.reduce((v = {}, k) => v[k], config);
        const Component = components[value];
        const callFetchData = C => // eslint-disable-line no-confusing-arrow
            C.fetchData
                ? C.fetchData(dispatch, renderProps, reqProps, options, ...args)
                : Promise.resolve();

        if (Component) {
            return callFetchData(Component);
        }
        else if (Fallback) {
            return callFetchData(Fallback);
        }

        return Promise.resolve();
    };

    IfConfig.displayName = `IfConfig(${key.join(',')})`;

    return withConfig('__config')(IfConfig);
};

export default ifConfig;
